import React, { useState, ChangeEvent, FormEvent } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

interface FormData {
  nombre: string;
  email: string;
  cartaPresentacion: string;
  curriculum: File | null;
}

export default function TrabajeConNosotros() {
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    email: '',
    cartaPresentacion: '',
    curriculum: null,
  });

  const [sending, setSending] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFormData({ ...formData, curriculum: e.target.files[0] });
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    setFormError('');

    // Aquí iría la lógica para enviar el formulario
    // Simulamos una espera y éxito para la demo
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setFormSubmitted(true);
    } catch (error) {
      setFormError('Ha ocurrido un error al enviar el formulario. Por favor, inténtelo de nuevo más tarde.');
    } finally {
      setSending(false);
    }
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <>
      <Helmet>
        <title>Trabaje con Nosotros | Innotec Food Equipment</title>
        <meta name="description" content="Únete al equipo de Innotec Food Equipment. Descubre nuestras oportunidades laborales y envía tu candidatura." />
      </Helmet>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <h1 className="text-4xl font-bold mb-6 text-white">Trabaje con Nosotros</h1>
          <div className="h-1 w-24 bg-[#FCAA0D]"></div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="prose prose-lg prose-invert max-w-none"
        >
          <section className="mb-12">
            <motion.p {...fadeIn} className="mb-6">
              En Innotec Food Equipment, estamos en constante crecimiento y actualmente nos encontramos en un emocionante proceso de expansión. Buscamos profesionales altamente capacitados y apasionados por la industria de maquinaria industrial para unirse a nuestro equipo. Si eres una persona motivada, con habilidades técnicas destacadas y una mentalidad orientada al cliente, ¡podrías ser parte de nuestro equipo!
            </motion.p>

            <motion.p {...fadeIn} className="mb-6">
              Tenemos diversas vacantes disponibles, desde perfiles Administrativos, Comerciales con idiomas, Técnicos en Mantenimiento, Informáticos, Abogados etc…
            </motion.p>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="my-10 rounded-lg overflow-hidden"
            >
              <img
                src="https://innotecfood.com/wp-content/uploads/2024/09/TRABAJO03-1024x768.jpg"
                alt="Equipo de Innotec Food Equipment"
                className="w-full h-auto object-cover rounded-lg transform hover:scale-105 transition-transform duration-500"
              />
            </motion.div>

            <motion.p {...fadeIn} className="mb-6">
              Nuestra plataforma B2B conectará a compradores y vendedores de maquinaria industrial a nivel global, ofreciendo un amplio catálogo de equipos, asesoramiento técnico especializado y un proceso de intermediación seguro y confiable. Dado que operamos en diferentes países y mercados, valoramos la diversidad cultural y lingüística en nuestro equipo.
            </motion.p>
          </section>

          <section className="mb-12">
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-2xl font-bold mb-6 text-[#FCAA0D]"
            >
              Idiomas Valorados
            </motion.h2>

            <motion.p {...fadeIn} className="mb-6">
              Debido a la naturaleza internacional de nuestro negocio, estamos buscando perfiles con habilidades lingüísticas en diferentes idiomas. Si eres un profesional con fluidez en uno o varios de los siguientes idiomas, nos encantaría contar con tus habilidades:
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="grid md:grid-cols-3 gap-6 my-8"
            >
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-[#FCAA0D] transition-colors duration-300">
                <h3 className="text-xl font-bold mb-3 text-[#FCAA0D]">Inglés</h3>
                <p>Conocimiento avanzado del idioma inglés, tanto oral como escrito, es fundamental para comunicarse con clientes y socios internacionales.</p>
              </div>

              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-[#FCAA0D] transition-colors duration-300">
                <h3 className="text-xl font-bold mb-3 text-[#FCAA0D]">Español</h3>
                <p>Siendo un proyecto B2B global, el dominio del español es esencial para el desarrollo de nuestras actividades en países de habla hispana.</p>
              </div>

              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-[#FCAA0D] transition-colors duration-300">
                <h3 className="text-xl font-bold mb-3 text-[#FCAA0D]">Otros Idiomas</h3>
                <p>Valoramos habilidades en otros idiomas relevantes para nuestros mercados objetivo, como alemán, francés, chino, ruso, entre otros.</p>
              </div>
            </motion.div>

            <motion.p {...fadeIn} className="mt-6">
              Si tienes habilidades lingüísticas en alguno de estos idiomas y estás interesado en formar parte de nuestro equipo, te invitamos a aplicar a nuestras vacantes disponibles.
            </motion.p>
          </section>

          <section className="mb-12">
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-2xl font-bold mb-6 text-[#FCAA0D]"
            >
              ¿Cómo aplicar?
            </motion.h2>

            <motion.p {...fadeIn} className="mb-8">
              Si estás interesado en unirte a Innotec Food Equipment y crees que cumples con los requisitos para alguna de nuestras vacantes, incluyendo habilidades lingüísticas, envíanos tu currículum vitae y una carta de presentación mediante el siguiente formulario. Nos pondremos en contacto contigo a la brevedad para discutir más detalles.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="bg-gray-800 rounded-lg p-8 border border-gray-700"
            >
              {formSubmitted ? (
                <div className="text-center py-8">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-green-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <h3 className="text-xl font-bold text-white mb-2">¡Solicitud enviada con éxito!</h3>
                  <p className="text-gray-300">Gracias por tu interés en unirte a nuestro equipo. Revisaremos tu candidatura y nos pondremos en contacto contigo pronto.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="nombre" className="block text-white mb-2">Nombre completo*</label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#FCAA0D] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-white mb-2">Correo electrónico*</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#FCAA0D] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="cartaPresentacion" className="block text-white mb-2">Carta de presentación*</label>
                    <textarea
                      id="cartaPresentacion"
                      name="cartaPresentacion"
                      value={formData.cartaPresentacion}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#FCAA0D] focus:border-transparent"
                      placeholder="Cuéntanos sobre ti, tu experiencia y por qué te gustaría unirte a nuestro equipo..."
                    />
                  </div>

                  <div>
                    <label htmlFor="curriculum" className="block text-white mb-2">Currículum vitae (PDF)*</label>
                    <div className="flex items-center justify-center w-full">
                      <label htmlFor="curriculum" className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-700 hover:bg-gray-600 transition-colors duration-300">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <svg className="w-8 h-8 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                          </svg>
                          <p className="mb-2 text-sm text-gray-400">
                            <span className="font-semibold">Haz clic para subir</span> o arrastra y suelta
                          </p>
                          <p className="text-xs text-gray-400">PDF (Máx. 10MB)</p>
                        </div>
                        <input
                          id="curriculum"
                          type="file"
                          name="curriculum"
                          accept=".pdf"
                          onChange={handleFileChange}
                          className="hidden"
                          required
                        />
                      </label>
                    </div>
                    {formData.curriculum && (
                      <p className="mt-2 text-sm text-green-400">
                        Archivo seleccionado: {formData.curriculum.name}
                      </p>
                    )}
                  </div>

                  {formError && (
                    <div className="p-4 bg-red-900/50 border border-red-500 rounded-lg text-white">
                      {formError}
                    </div>
                  )}

                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={sending}
                      className={`w-full py-3 px-6 rounded-lg font-medium text-white bg-[#FCAA0D] hover:bg-[#E69C00] transition-colors duration-300 ${sending ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {sending ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Enviando...
                        </span>
                      ) : "Enviar solicitud"}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </section>

          <section className="mb-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="bg-gray-800/50 border border-gray-700 rounded-lg p-6"
            >
              <h3 className="text-lg font-semibold text-[#FCAA0D] mb-3">Nota importante</h3>
              <p className="text-sm">
                Al enviar tu solicitud, aceptas nuestra política de privacidad y el tratamiento de tus datos personales con el único propósito de evaluar tu candidatura. Tus datos serán tratados con la máxima confidencialidad y no serán compartidos con terceros sin tu consentimiento expreso.
              </p>
            </motion.div>
          </section>
        </motion.div>
      </div>
    </>
  );
}
